import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Languages } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import { errorAlert } from '@xdocs/schemas';
import type { JobAccepted } from '@xdocs/schemas';
import { requestTyped } from '@xdocs/ws-protocol';
import { Badge, Chip, ListRow, cn, useI18n, useToast } from '@xdocs/ui';
import { useJobSnapshot } from '../jobs/index.js';
import { useWs } from '../../ws/WsProvider.js';
import { EnhancementPanelShell } from './EnhancementPanelShell.js';
import type { EnhancementPanelPhase } from './EnhancementPanelShell.js';
import { TRANSLATE_LANGUAGE_OPTIONS } from './translateLanguages.js';
import { useActionEstimate } from './useActionEstimate.js';
import { useEnhancementT } from './useEnhancementT.js';
import styles from './TranslatePanel.module.css';

export interface TranslatePanelProps {
  documentId: string;
  /** Required — names the asset in the done/error toasts (§16.1). */
  documentTitle: string;
  versionId: string;
  className?: string | undefined;
}

/**
 * F17 — document translations (PRO). Self-contained trigger + panel;
 * multi-select target languages (F48 AC: "cost per language" — `units` in
 * the estimate scales with the selection, F17 AC: "multi-language batch").
 */
export function TranslatePanel({ documentId, documentTitle, versionId, className }: TranslatePanelProps): ReactElement {
  const { t } = useEnhancementT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const { wsClient } = useWs();

  const [open, setOpen] = useState(false);
  // Only ever 'form' | 'starting' | 'upsell' | 'running' — terminal
  // 'done'/'error' is DERIVED from the live job snapshot below (react-hooks
  // purity: never setState synchronously inside an effect).
  const [phase, setPhase] = useState<EnhancementPanelPhase>('form');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const hasSelection = targetLanguages.length > 0;
  const { estimate } = useActionEstimate('translate.estimate', open && hasSelection ? { documentId, versionId, targetLanguages } : null);
  const { snapshot } = useJobSnapshot(jobId);

  const jobSucceeded = phase === 'running' && snapshot?.job.status === 'succeeded';
  const jobFailedTerminal =
    phase === 'running' && snapshot?.job.status === 'failed' && snapshot.job.attempts >= snapshot.job.maxAttempts;
  const displayPhase: EnhancementPanelPhase = jobSucceeded ? 'done' : jobFailedTerminal ? 'error' : phase;
  const displayErrorMessage = jobFailedTerminal ? (snapshot?.job.lastError ?? 'errors:code.INTERNAL') : submitError;

  const toastedForJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (jobSucceeded && jobId && toastedForJobRef.current !== jobId) {
      pushToast(assetToast(locale, 'success', t('enhancement:translate.success'), documentTitle));
      toastedForJobRef.current = jobId;
    }
  }, [jobSucceeded, jobId, pushToast, locale, t, documentTitle]);

  function resetForm(): void {
    setPhase('form');
    setJobId(null);
    setSubmitError(null);
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) {
      resetForm();
      setTargetLanguages([]);
    }
  }

  function toggleLanguage(code: string): void {
    setTargetLanguages((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  }

  function handleStart(): void {
    if (!hasSelection) return;
    setPhase('starting');
    requestTyped(wsClient, 'document.translate', { documentId, versionId, targetLanguages })
      .then((accepted: JobAccepted) => {
        setJobId(accepted.jobId);
        setPhase('running');
      })
      .catch((err: unknown) => {
        const parsed = errorAlert.safeParse(err);
        if (parsed.success && parsed.data.code === 'TIER_REQUIRED') {
          setPhase('upsell');
          return;
        }
        setPhase('error');
        setSubmitError(parsed.success ? parsed.data.message : 'errors:code.INTERNAL');
      });
  }

  const creditsLabel = estimate ? t('enhancement:cost.credits', { credits: estimate.credits }) : undefined;

  return (
    <>
      <ListRow
        icon={<Languages size={16} aria-hidden="true" />}
        title={t('enhancement:translate.title')}
        trailing={<Badge tone="pro">{t('enhancement:pro.badge')}</Badge>}
        onClick={() => setOpen(true)}
        className={cn(className)}
      />
      <EnhancementPanelShell
        open={open}
        onOpenChange={handleOpenChange}
        title={t('enhancement:translate.title')}
        blurb={t('enhancement:translate.blurb')}
        phase={displayPhase}
        jobId={jobId}
        doneTitle={t('enhancement:translate.doneTitle')}
        errorMessage={displayErrorMessage}
        ctaLabel={t('enhancement:translate.cta')}
        creditsLabel={creditsLabel}
        ctaDisabled={!hasSelection}
        onStart={handleStart}
        onMaybeLater={() => handleOpenChange(false)}
        onDone={() => handleOpenChange(false)}
        onRetryFromError={resetForm}
      >
        <div className={styles['field']}>
          <span>{t('enhancement:translate.targetLanguage')}</span>
          <div className={styles['languageGrid']} role="group" aria-label={t('enhancement:translate.targetLanguage')}>
            {TRANSLATE_LANGUAGE_OPTIONS.map((option) => (
              <Chip key={option.code} active={targetLanguages.includes(option.code)} onClick={() => toggleLanguage(option.code)}>
                {option.label}
              </Chip>
            ))}
          </div>
          {!hasSelection && <p className={styles['validation']}>{t('enhancement:translate.noLanguages')}</p>}
        </div>
      </EnhancementPanelShell>
    </>
  );
}
